
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function RetroVintageHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-[#EAE3D9] text-[#4A3F35] font-serif">
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Roboto+Mono&display=swap');
        .font-display { font-family: 'Playfair Display', serif; }
        .font-mono { font-family: 'Roboto Mono', monospace; }
      `}</style>
      <header className="container mx-auto px-6 py-5 flex justify-between items-center border-b-2 border-[#D2C5B3]">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#BF4A2A]">
          The Time Capsule
        </h2>
        <nav className="hidden md:flex items-center gap-8 font-mono text-sm">
          <a href="#" className="hover:text-[#BF4A2A]">
            Apparel
          </a>
          <a href="#" className="hover:text-[#BF4A2A]">
            Homeware
          </a>
          <a href="#" className="hover:text-[#BF4A2A]">
            Music
          </a>
          <Button variant="ghost" className="p-2 hover:bg-[#D2C5B3]">
            <ShoppingCart />
          </Button>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            className="p-2 hover:bg-[#D2C5B3]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="md:hidden bg-[#EAE3D9]/95 border-b-2 border-[#D2C5B3]">
          <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg font-mono">
            <a href="#" className="hover:text-[#BF4A2A]">Apparel</a>
            <a href="#" className="hover:text-[#BF4A2A]">Homeware</a>
            <a href="#" className="hover:text-[#BF4A2A]">Music</a>
            <Button variant="outline" className="w-full mt-2 border-[#D2C5B3]">
                Cart <ShoppingCart className="ml-2" />
            </Button>
          </nav>
        </div>
      )}


      <main>
        <section className="relative text-center py-20 px-6">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/old-wall.png')] opacity-20"></div>
          <div className="relative">
            <h1 className="font-display text-5xl md:text-8xl font-bold text-[#4A3F35]">
              Goods from a Golden Age
            </h1>
            <p className="mt-4 font-mono text-lg max-w-xl mx-auto">
              Authentic vintage finds and retro-inspired originals. Curated for
              the nostalgic soul.
            </p>
            <Button className="mt-8 bg-[#BF4A2A] text-[#F5F1EB] hover:bg-[#A54024] rounded-sm font-mono uppercase tracking-widest px-8 py-6">
              Shop New Arrivals
            </Button>
          </div>
        </section>

        <section className="container mx-auto px-6 py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative group h-96">
              <Image
                src="https://picsum.photos/seed/retro1/600/800"
                alt="A person wearing a 70s style patterned shirt and bell bottoms"
                layout="fill"
                objectFit="cover"
                className="sepia-[.5] group-hover:sepia-0 transition-all duration-300"
                data-ai-hint="vintage fashion"
              />
              <div className="absolute bottom-0 left-0 bg-[#EAE3D9] px-4 py-2 font-display text-xl">
                Apparel
              </div>
            </div>
            <div className="relative group h-96 lg:mt-12">
              <Image
                src="https://picsum.photos/seed/retro2/600/800"
                alt="A vintage record player with a vinyl spinning"
                layout="fill"
                objectFit="cover"
                className="sepia-[.5] group-hover:sepia-0 transition-all duration-300"
                data-ai-hint="record player"
              />
              <div className="absolute bottom-0 left-0 bg-[#EAE3D9] px-4 py-2 font-display text-xl">
                Audio
              </div>
            </div>
            <div className="relative group h-96">
              <Image
                src="https://picsum.photos/seed/retro3/600/800"
                alt="An old film camera on a stack of books"
                layout="fill"
                objectFit="cover"
                className="sepia-[.5] group-hover:sepia-0 transition-all duration-300"
                data-ai-hint="film camera"
              />
              <div className="absolute bottom-0 left-0 bg-[#EAE3D9] px-4 py-2 font-display text-xl">
                Cameras
              </div>
            </div>
            <div className="relative group h-96 lg:mt-12">
              <Image
                src="https://picsum.photos/seed/retro4/600/800"
                alt="Retro-style kitchenware like a colorful mixer and canisters"
                layout="fill"
                objectFit="cover"
                className="sepia-[.5] group-hover:sepia-0 transition-all duration-300"
                data-ai-hint="retro kitchenware"
              />
              <div className="absolute bottom-0 left-0 bg-[#EAE3D9] px-4 py-2 font-display text-xl">
                Homeware
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#D2C5B3] py-20 px-6">
          <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="md:pr-8">
              <h2 className="font-display text-4xl font-bold">Built to Last.</h2>
              <p className="mt-4 font-mono">
                We believe in items with a story. Our collection is carefully
                selected for its quality, character, and timeless appeal. These
                aren't just things; they're keepers.
              </p>
            </div>
            <div>
              <Image
                src="https://picsum.photos/seed/retro-craft/1000/700"
                alt="Hands of a craftsman restoring an old wooden chair"
                width={1000}
                height={700}
                className="w-full h-auto"
                data-ai-hint="craftsman hands"
              />
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-[#4A3F35] text-[#EAE3D9] py-16 px-6 font-mono">
        <div className="container mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-display text-2xl font-bold mb-4">
              The Time Capsule
            </h3>
            <p className="text-sm text-[#D2C5B3]">
              Purveyors of fine vintage goods.
            </p>
          </div>
          <div>
            <h3 className="font-bold tracking-widest uppercase mb-4">
              Explore
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#" className="hover:text-white">
                  About Us
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Journal
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white">
                  Contact
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold tracking-widest uppercase mb-4">
              Newsletter
            </h3>
            <p className="text-sm text-[#D2C5B3] mb-4">
              Get weekly digests of new arrivals and special finds.
            </p>
            <div className="flex">
              <input
                type="email"
                placeholder="your.email@example.com"
                className="w-full bg-[#6B5E51] border border-[#887A6D] p-2 text-sm placeholder-[#B8A899]"
              />
              <button className="bg-[#BF4A2A] text-white p-2">Join</button>
            </div>
          </div>
        </div>
        <div className="container mx-auto text-center mt-12 border-t border-[#6B5E51] pt-8 text-xs text-[#A49486]">
          <p>&copy; {new Date().getFullYear()} The Time Capsule Co.</p>
        </div>
      </footer>
    </div>
  );
}
